import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  GlobalOutlined,
  PlayCircleOutlined,
  PoweroffOutlined,
  ReloadOutlined,
  SearchOutlined,
  ShoppingOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  DeleteOutlined,
  CodeOutlined,
  DiffOutlined,
  TeamOutlined,
  RadarChartOutlined,
  SwapOutlined,
  AppstoreOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { browserAPI as localBrowserAPI, remoteBrowserAPI, forwardAPI } from '@/api/endpoints/browser.js';
import styles from '@/styles/pages/browser/BrowserPage.module.css';

const POLL_INTERVAL_MS = 1500;

const QUEUE_STATUS_LABEL = {
  queued: 'В очереди',
  processing: 'Выполняется',
  done: 'Готово',
  error: 'Ошибка',
};

const QUEUE_KIND_LABEL = {
  product: 'Товар',
  position: 'Позиция',
  category: 'Позиция в категории',
  compare: 'Сравнение',
  recommendations: 'Рекомендации',
  scan: 'Скан конкурентов',
  mutual: 'Взаимные рекомендации',
};

const formatUptime = (seconds) => {
  if (seconds == null) return '—';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}ч ${m}м ${s}с`;
  if (m > 0) return `${m}м ${s}с`;
  return `${s}с`;
};

const browserDotClass = (browser) => {
  if (browser.blocked) return 'browserDot_blocked';
  if (browser.running) return 'browserDot_running';
  return 'browserDot_stopped';
};

const TARGETS = [
  { key: 'local', label: 'Локальные' },
  { key: 'remote', label: 'Удалённые' },
];

const BrowserPage = () => {
  const [target, setTarget] = useState(() => {
    try { return localStorage.getItem('ozonBrowserTarget') === 'remote' ? 'remote' : 'local'; } catch { return 'local'; }
  });
  const [forwardEnabled, setForwardEnabled] = useState(false);
  const browserAPI = target === 'remote' ? remoteBrowserAPI : localBrowserAPI;
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [queueItems, setQueueItems] = useState([]);

  const [useProxy, setUseProxy] = useState(true);
  const [loadingAction, setLoadingAction] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);

  const [productId, setProductId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryUrl, setCategoryUrl] = useState('');
  const [competitorIds, setCompetitorIds] = useState('');
  const [submittingProduct, setSubmittingProduct] = useState(false);
  const [submittingPosition, setSubmittingPosition] = useState(false);
  const [submittingCategory, setSubmittingCategory] = useState(false);
  const [submittingCompare, setSubmittingCompare] = useState(false);
  const [submittingRecommendations, setSubmittingRecommendations] = useState(false);
  const [submittingScan, setSubmittingScan] = useState(false);
  const [submittingMutual, setSubmittingMutual] = useState(false);

  const sinceIdRef = useRef(0);
  const logsConsoleRef = useRef(null);
  const proxyTouchedRef = useRef(false);

  useEffect(() => {
    document.title = 'Ozon Browser | DevSpace';
  }, []);

  const poll = useCallback(async () => {
    const [statusRes, logsRes, queueRes] = await Promise.all([
      browserAPI.status_get(false),
      browserAPI.logs_get(sinceIdRef.current, false),
      browserAPI.queue_get(false),
    ]);

    if (statusRes.success && statusRes.data) {
      setStatus(statusRes.data);
      if (!proxyTouchedRef.current) {
        setUseProxy(statusRes.data.use_proxy);
      }
    }

    if (logsRes.success && logsRes.data?.logs?.length) {
      const newLogs = logsRes.data.logs;
      sinceIdRef.current = newLogs[newLogs.length - 1].id;
      setLogs((prev) => [...prev, ...newLogs].slice(-2000));
    }

    if (queueRes.success && queueRes.data) {
      setQueueItems(queueRes.data);
    }
  }, [browserAPI]);

  useEffect(() => {
    forwardAPI.get().then((res) => {
      if (res.success && res.data) setForwardEnabled(res.data.enabled);
    });
  }, []);

  const handleForwardToggle = async () => {
    const res = await forwardAPI.set(!forwardEnabled);
    if (res.success && res.data) setForwardEnabled(res.data.enabled);
  };

  const handleTargetChange = (key) => {
    if (key === target) return;
    try { localStorage.setItem('ozonBrowserTarget', key); } catch { /* ignore */ }
    sinceIdRef.current = 0;
    proxyTouchedRef.current = false;
    setStatus(null);
    setLogs([]);
    setQueueItems([]);
    setInitialLoading(true);
    setTarget(key);
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      await poll();
      if (!cancelled) setInitialLoading(false);
    })();

    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [poll]);

  useEffect(() => {
    const el = logsConsoleRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [logs]);

  const handleProxyToggle = () => {
    proxyTouchedRef.current = true;
    setUseProxy((prev) => !prev);
  };

  const handleStart = async () => {
    setLoadingAction('start');
    await browserAPI.start_post(useProxy, true);
    setLoadingAction('');
    await poll();
  };

  const handleStop = async () => {
    setLoadingAction('stop');
    await browserAPI.stop_post(true);
    setLoadingAction('');
    await poll();
  };

  const handleRestart = async () => {
    setLoadingAction('restart');
    await browserAPI.restart_post(useProxy, true);
    setLoadingAction('');
    await poll();
  };

  const handleAutoRestart = async () => {
    setLoadingAction('autoRestart');
    if (autoRestartActive) {
      await browserAPI.rolling_restart_stop_post(true);
    } else {
      await browserAPI.rolling_restart_start_post(180, true, true);
    }
    setLoadingAction('');
    await poll();
  };

  const handleClearLogs = () => {
    setLogs([]);
  };

  const handleProductQuery = async (e) => {
    e.preventDefault();
    if (!productId.trim() || submittingProduct) return;
    setSubmittingProduct(true);
    const res = await browserAPI.product_query_post(productId.trim(), false);
    setSubmittingProduct(false);
    if (res.success) await poll();
  };

  const handlePositionQuery = async (e) => {
    e.preventDefault();
    if (!productId.trim() || !searchQuery.trim() || submittingPosition) return;
    setSubmittingPosition(true);
    const res = await browserAPI.position_query_post(productId.trim(), searchQuery.trim(), false);
    setSubmittingPosition(false);
    if (res.success) await poll();
  };

  const handleCategoryQuery = async (e) => {
    e.preventDefault();
    if (!productId.trim() || !categoryUrl.trim() || submittingCategory) return;
    setSubmittingCategory(true);
    const res = await browserAPI.category_query_post(productId.trim(), categoryUrl.trim(), false);
    setSubmittingCategory(false);
    if (res.success) await poll();
  };

  const handleCompareQuery = async (e) => {
    e.preventDefault();
    if (!productId.trim() || submittingCompare) return;
    setSubmittingCompare(true);
    const res = await browserAPI.compare_query_post(productId.trim(), false);
    setSubmittingCompare(false);
    if (res.success) await poll();
  };

  const handleRecommendationsQuery = async (e) => {
    e.preventDefault();
    if (!productId.trim() || submittingRecommendations) return;
    setSubmittingRecommendations(true);
    const res = await browserAPI.recommendations_query_post(productId.trim(), false);
    setSubmittingRecommendations(false);
    if (res.success) await poll();
  };

  const parsedCompetitorIds = competitorIds
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

  const handleScanQuery = async (e) => {
    e.preventDefault();
    if (!productId.trim() || !parsedCompetitorIds.length || submittingScan) return;
    setSubmittingScan(true);
    const res = await browserAPI.scan_query_post(productId.trim(), parsedCompetitorIds, false);
    setSubmittingScan(false);
    if (res.success) await poll();
  };

  const handleMutualQuery = async (e) => {
    e.preventDefault();
    if (!productId.trim() || submittingMutual) return;
    setSubmittingMutual(true);
    const res = await browserAPI.mutual_query_post(productId.trim(), false);
    setSubmittingMutual(false);
    if (res.success) await poll();
  };

  const running = status?.running ?? false;
  const canQuery = running || (target === 'local' && forwardEnabled);
  const busy = loadingAction !== '';
  const orderedQueue = [...queueItems].reverse();
  const browsers = status?.browsers ?? [];
  const runningCount = browsers.filter((b) => b.running).length;
  const autoRestartActive = status?.rolling_restart?.enabled ?? false;
  const autoRestartInterval = status?.rolling_restart?.interval_seconds;

  if (initialLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.bgGradient}></div>
        <div className={styles.bgGrid}></div>
        <div className={styles.loadingContainer}>
          <LoadingOutlined className={styles.loadingSpinner} />
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.bgGradient}></div>
      <div className={styles.bgGrid}></div>

      <div className={styles.content}>
        {/* Шапка */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <GlobalOutlined className={styles.headerIcon} />
            <h1 className={styles.title}>Ozon Browser</h1>
            <span className={`${styles.statusBadge} ${running ? styles.statusRunning : styles.statusStopped}`}>
              {running ? 'Работает' : 'Остановлен'}
            </span>
          </div>

          <div className={styles.headerStats}>
            <div className={styles.headerStat}>
              <span className={styles.headerStatValue}>{runningCount}/{browsers.length || '—'}</span>
              <span className={styles.headerStatLabel}>Браузеров</span>
            </div>
            <div className={styles.headerStatDivider}></div>
            <div className={styles.headerStat}>
              <span className={styles.headerStatValue}>{status?.queue_length ?? 0}</span>
              <span className={styles.headerStatLabel}>В очереди</span>
            </div>
            <div className={styles.headerStatDivider}></div>
            <div className={styles.headerStat}>
              <span className={styles.headerStatValue}>{status?.total_403_failovers ?? 0}</span>
              <span className={styles.headerStatLabel}>Failover 403</span>
            </div>
          </div>
        </div>

        <div className={styles.targetSwitch}>
          {TARGETS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`${styles.targetButton} ${target === t.key ? styles.targetButtonActive : ''}`}
              onClick={() => handleTargetChange(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {target === 'local' && (
          <div className={styles.proxyToggle} style={{ marginBottom: 'var(--space-4)' }}>
            <button
              type="button"
              className={`${styles.toggleTrack} ${forwardEnabled ? styles.toggleOn : ''}`}
              onClick={handleForwardToggle}
              aria-pressed={forwardEnabled}
            >
              <span className={styles.toggleThumb}></span>
            </button>
            <div className={styles.toggleLabel}>
              <span>Перенаправлять запросы на удалённые браузеры</span>
              <span className={styles.toggleHint}>все новые запросы к локальному API уйдут на удалённый пул; результаты — во вкладке «Удалённые»</span>
            </div>
          </div>
        )}

        {/* Панель управления */}
        <div className={styles.controlPanel}>
          <div className={styles.proxyToggle}>
            <button
              type="button"
              className={`${styles.toggleTrack} ${useProxy ? styles.toggleOn : ''}`}
              onClick={handleProxyToggle}
              aria-pressed={useProxy}
            >
              <span className={styles.toggleThumb}></span>
            </button>
            <div className={styles.toggleLabel}>
              <span>Использовать прокси</span>
              <span className={styles.toggleHint}>применится при следующем старте/рестарте</span>
            </div>
          </div>

          <div className={styles.controlButtons}>
            <button
              className={`${styles.actionButton} ${styles.startButton}`}
              onClick={handleStart}
              disabled={busy || running}
            >
              {loadingAction === 'start' ? <LoadingOutlined /> : <PlayCircleOutlined />}
              Старт
            </button>
            <button
              className={`${styles.actionButton} ${styles.stopButton}`}
              onClick={handleStop}
              disabled={busy || !canQuery}
            >
              {loadingAction === 'stop' ? <LoadingOutlined /> : <PoweroffOutlined />}
              Стоп
            </button>
            <button
              className={`${styles.actionButton} ${styles.restartButton}`}
              onClick={handleRestart}
              disabled={busy}
            >
              {loadingAction === 'restart' ? <LoadingOutlined /> : <ReloadOutlined />}
              Перезапуск
            </button>
            <button
              className={`${styles.actionButton} ${styles.autoRestartButton} ${autoRestartActive ? styles.autoRestartActive : ''}`}
              onClick={handleAutoRestart}
              disabled={busy}
              title={autoRestartActive ? `Авто-рестарт каждые ${autoRestartInterval}с · нажми чтобы отключить` : 'Включить авто-рестарт каждые 3 мин (по очереди)'}
            >
              {loadingAction === 'autoRestart' ? <LoadingOutlined /> : <SyncOutlined spin={autoRestartActive} />}
              {autoRestartActive
                ? `Авто · ${Math.round(autoRestartInterval / 60)}м`
                : 'Авто-рестарт'}
            </button>
          </div>

        </div>

        {/* Браузеры пула */}
        <div className={styles.panel} style={{ marginBottom: 'var(--space-6)' }}>
          <div className={styles.panelHeader}>
            <h2><GlobalOutlined /> Браузеры</h2>
            <span className={styles.panelCount}>{runningCount}/{browsers.length}</span>
          </div>
          <div className={styles.browserCards}>
            {browsers.map((b) => (
              <div key={b.index} className={styles.browserCard}>
                <div className={styles.browserCardHeader}>
                  <span className={`${styles.browserDot} ${styles[browserDotClass(b)]}`}></span>
                  <span className={styles.browserIndex}>Браузер {b.index}</span>
                  {b.blocked && (
                    <span className={styles.browserBlockedBadge}>Рестарт после 403</span>
                  )}
                </div>
                <dl className={styles.browserStatsGrid}>
                  <dt>PID</dt>
                  <dd>{b.pid ?? '—'}</dd>
                  <dt>Аптайм</dt>
                  <dd>{formatUptime(b.uptime_seconds)}</dd>
                  <dt>В очереди</dt>
                  <dd>{b.queue_length}</dd>
                  <dt>Авто-рестарты</dt>
                  <dd>{b.auto_restarts}</dd>
                </dl>
                {b.last_event && (
                  <div className={styles.lastEvent}>
                    <code>{JSON.stringify(b.last_event)}</code>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Основная сетка: запрос+очередь / логи */}
        <div className={styles.mainGrid}>
          <div className={styles.leftColumn}>
            {/* Ручной запрос */}
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <h2><ShoppingOutlined /> Ручной запрос</h2>
              </div>
              <div className={styles.queryForms}>
                <input
                  className={styles.input}
                  placeholder="Артикул товара (productId)"
                  value={productId}
                  onChange={(e) => setProductId(e.target.value)}
                />
                <input
                  className={styles.input}
                  placeholder="Поисковый запрос (для позиции)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <input
                  className={styles.input}
                  placeholder="Ссылка на категорию (для позиции в категории)"
                  value={categoryUrl}
                  onChange={(e) => setCategoryUrl(e.target.value)}
                />
                <input
                  className={styles.input}
                  placeholder="Артикулы конкурентов через запятую (для скана)"
                  value={competitorIds}
                  onChange={(e) => setCompetitorIds(e.target.value)}
                />
                <div className={styles.queryButtons}>
                  <button
                    className={styles.queryButton}
                    onClick={handleProductQuery}
                    disabled={!productId.trim() || submittingProduct || !canQuery}
                  >
                    <ShoppingOutlined /> Получить товар
                  </button>
                  <button
                    className={styles.queryButton}
                    onClick={handlePositionQuery}
                    disabled={!productId.trim() || !searchQuery.trim() || submittingPosition || !canQuery}
                  >
                    <SearchOutlined /> Позиция в поиске
                  </button>
                  <button
                    className={styles.queryButton}
                    onClick={handleCategoryQuery}
                    disabled={!productId.trim() || !categoryUrl.trim() || submittingCategory || !canQuery}
                  >
                    <AppstoreOutlined /> Позиция в категории
                  </button>
                  <button
                    className={styles.queryButton}
                    onClick={handleCompareQuery}
                    disabled={!productId.trim() || submittingCompare || !canQuery}
                  >
                    <DiffOutlined /> Проверить сравнение
                  </button>
                  <button
                    className={styles.queryButton}
                    onClick={handleMutualQuery}
                    disabled={!productId.trim() || submittingMutual || !canQuery}
                  >
                    <SwapOutlined /> Взаимные рекомендации
                  </button>
                  <button
                    className={styles.queryButton}
                    onClick={handleRecommendationsQuery}
                    disabled={!productId.trim() || submittingRecommendations || !canQuery}
                  >
                    <TeamOutlined /> Рекомендации
                  </button>
                  <button
                    className={styles.queryButton}
                    onClick={handleScanQuery}
                    disabled={!productId.trim() || !parsedCompetitorIds.length || submittingScan || !canQuery}
                  >
                    <RadarChartOutlined /> Скан конкурентов
                  </button>
                </div>
                <span className={styles.queryHint}>
                  «Взаимные рекомендации» сама берёт конкурентов из блоков рекомендаций товара и сразу
                  проверяет обратную сторону — поле со списком конкурентов нужно только для ручного «Скана»
                </span>
                {!canQuery && (
                  <span className={styles.queryHint}>Запросы обрабатываются, только пока браузер запущен</span>
                )}
              </div>
            </div>

            {/* Очередь */}
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <h2><ClockCircleOutlined /> Очередь запросов</h2>
                <span className={styles.panelCount}>{queueItems.length}</span>
              </div>
              <div className={styles.queueList}>
                {orderedQueue.length === 0 ? (
                  <div className={styles.emptyState}>
                    <span>Пока не было ни одного запроса</span>
                  </div>
                ) : (
                  orderedQueue.map((item) => (
                    <div key={item.id} className={styles.queueItem}>
                      <div className={styles.queueItemHeader}>
                        <span className={styles.queueItemId}>#{item.id}</span>
                        <span className={styles.queueItemKind}>
                          {QUEUE_KIND_LABEL[item.kind] ?? item.kind}
                        </span>
                        <span className={`${styles.queueStatusBadge} ${styles[`queueStatus_${item.status}`]}`}>
                          {item.status === 'processing' && <LoadingOutlined />}
                          {item.status === 'done' && <CheckCircleOutlined />}
                          {item.status === 'error' && <CloseCircleOutlined />}
                          {QUEUE_STATUS_LABEL[item.status]}
                          {item.status === 'queued' && item.position ? ` (#${item.position})` : ''}
                        </span>
                      </div>
                      <div className={styles.queueItemPayload}>
                        {item.payload?.productId}
                        {item.payload?.query ? ` — "${item.payload.query}"` : ''}
                        {item.payload?.categoryUrl ? ` — ${item.payload.categoryUrl}` : ''}
                        {item.payload?.competitorIds ? ` vs [${item.payload.competitorIds.join(', ')}]` : ''}
                      </div>
                      {item.attempts?.length > 1 && (
                        <div className={styles.queueItemAttempts}>
                          перекинут: {item.attempts.map((a) => `браузер ${a.browser}${a.error ? ` (${a.error})` : ''}`).join(' → ')}
                        </div>
                      )}
                      {(item.status === 'done' || item.status === 'error') && item.result && (
                        <details className={styles.queueItemResult}>
                          <summary>JSON-ответ</summary>
                          <pre>{JSON.stringify(item.result, null, 2)}</pre>
                        </details>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Консоль логов */}
          <div className={styles.logsPanel}>
            <div className={styles.panelHeader}>
              <h2><CodeOutlined /> Логи процесса</h2>
              <button className={styles.clearButton} onClick={handleClearLogs}>
                <DeleteOutlined /> Очистить
              </button>
            </div>
            <div className={styles.logsConsole} ref={logsConsoleRef}>
              {logs.length === 0 ? (
                <div className={styles.emptyState}>
                  <span>Логов пока нет — запустите браузер</span>
                </div>
              ) : (
                logs.map((entry) => (
                  <div
                    key={entry.id}
                    className={`${styles.logLine} ${entry.source === 'stderr' ? styles.logStderr : styles.logStdout}`}
                  >
                    <span className={styles.logTime}>
                      {new Date(entry.ts).toLocaleTimeString('ru-RU')}
                    </span>
                    <span className={`${styles.logBrowserTag} ${styles[`logBrowser${entry.browser % 3}`]}`}>
                      [{entry.browser}]
                    </span>
                    <span className={styles.logText}>{entry.text}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrowserPage;
